import React from 'react';
import { motion } from 'motion/react';
import { AlertTriangle } from 'lucide-react';

// Generic loading spinner
export const LoadingSpinner: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}> = ({ size = 'md', text = 'Loading...' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-4">
      <motion.div
        className={`${sizeClasses[size]} border-4 border-[#283673] border-t-transparent rounded-full animate-spin`}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      {text && (
        <motion.p
          className="text-gray-600 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

// Page loading component
export const PageLoading: React.FC<{ message?: string }> = ({
  message = 'Loading page...',
}) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F9FAFC]">
    <div className="text-center">
      <LoadingSpinner size="lg" text={message} />
    </div>
  </div>
);

// Component loading wrapper
export const ComponentLoading: React.FC<{
  children: React.ReactNode;
  isLoading: boolean;
  loadingText?: string;
  className?: string;
}> = ({ children, isLoading, loadingText = 'Loading...', className = '' }) => {
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <LoadingSpinner text={loadingText} />
      </div>
    );
  }

  return <>{children}</>;
};

// Skeleton loading components
export const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
    <div className="flex items-center space-x-4 mb-4">
      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-gray-200 rounded"></div>
      <div className="h-3 bg-gray-200 rounded w-5/6"></div>
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
    <div className="p-4 border-b">
      <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
    </div>
    <div className="divide-y">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="p-4 flex items-center space-x-4 animate-pulse">
          <div className="w-8 h-8 bg-gray-200 rounded"></div>
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
          <div className="w-16 h-6 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  </div>
);

// Route-specific loading components
export const DashboardLoading = () => (
  <PageLoading message="Loading dashboard..." />
);

export const ServicesLoading = () => (
  <PageLoading message="Loading services..." />
);

export const OrdersLoading = () => <PageLoading message="Loading orders..." />;

export const ProfileLoading = () => (
  <PageLoading message="Loading profile..." />
);

// Error loading component
export const ErrorLoading: React.FC<{
  error: string;
  onRetry?: () => void;
}> = ({ error, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F9FAFC]">
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load</h3>
      <p className="text-sm text-gray-500 mb-4">{error}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-[#283673] text-white rounded-md hover:bg-[#1e2a4f] transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  </div>
);
